import { useCallback, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { getErrMessage, to } from '../../../utilities/PromiseUtilities';

/**
 * Shared action helper utilities for infrastructure components (device and virtual).
 * Provides common patterns for selection management, payload building, and action execution.
 */

/**
 * Builds a flat selection payload for detail-level actions (ENABLE, DISABLE, DELETE).
 * Used for single-record operations on detail pages.
 *
 * @param {string} entityId - Entity UUID
 * @returns {Object} Flat selection payload with selection array and inverted flag
 */
export const buildDetailActionPayload = (entityId) => ({
    selection: [entityId],
    inverted: false,
});

/**
 * Builds a nested selection payload for RESCAN action.
 * RESCAN uses a different API structure with selectionParams wrapper.
 *
 * @param {string} entityId - Entity UUID
 * @returns {Object} Nested selection payload with selectionParams wrapper
 */
export const buildRescanPayload = (entityId) => ({
    selectionParams: {
        inverted: false,
        selection: [entityId],
        count: 1,
        valid: true,
    },
});

/**
 * Builds a bulk action payload from table selection state.
 * Merges table selection with optional extra payload fields.
 *
 * @param {Object} tableSelection - Current table selection state {selection, inverted}
 * @param {Object} extraPayload - Additional payload fields to merge
 * @returns {Object} Combined payload for bulk action API
 */
export const buildBulkActionPayload = (tableSelection, extraPayload = {}) => ({
    ...tableSelection,
    ...extraPayload,
});

/**
 * Checks if a table selection is empty.
 * A selection is considered empty if it's null, has no items in selection array,
 * and is not inverted (inverted selections are never empty).
 *
 * @param {Object} tableSelection - Table selection state {selection, inverted}
 * @returns {boolean} True if selection is empty
 */
export const isSelectionEmpty = (tableSelection) => {
    if (!tableSelection) return true;
    return isEmpty(tableSelection.selection) && !tableSelection.inverted;
};

/**
 * Builds query parameters with JSON-stringified query object.
 * Common pattern for infrastructure API calls.
 *
 * @param {Object} queryObject - Query object to stringify
 * @param {string} requestParams - Optional pre-stringified request params
 * @returns {Object} Query parameters ready for API call
 */
export const buildInfrastructureQueryParams = (queryObject, requestParams = null) => {
    const params = {
        query: JSON.stringify(queryObject),
    };

    if (requestParams) {
        params.requestParams = requestParams;
    }

    return params;
};

/**
 * Generic action executor with standardized error handling and toast notifications.
 * Handles the common pattern of: validate -> execute -> handle errors -> show success.
 *
 * @param {Object} params - Execution parameters
 * @param {Function} params.dataService - API service instance
 * @param {string} params.actionUrl - URL to post action to
 * @param {Object} params.payload - Action payload
 * @param {Object} params.queryParams - Optional query parameters
 * @param {Function} params.onSuccess - Optional callback after successful action
 * @param {Function} params.onError - Optional error callback (receives error message)
 * @returns {Promise<boolean>} True if action succeeded, false otherwise
 */
export const executeInfrastructureAction = async ({
    dataService,
    actionUrl,
    payload,
    queryParams = null,
    onSuccess,
    onError,
}) => {
    if (isEmpty(actionUrl)) {
        const errorMsg = 'Invalid action URL';
        if (onError) onError(errorMsg);
        return false;
    }

    const [error] = await to(
        queryParams ? dataService.post(actionUrl, payload, queryParams) : dataService.post(actionUrl, payload)
    );

    if (error) {
        const errorMsg = getErrMessage(error);
        if (onError) onError(errorMsg);
        return false;
    }

    if (onSuccess) {
        await onSuccess();
    }

    return true;
};

/**
 * Hook for managing selection state with ref and disabled state.
 * Returns selection ref, disabled state, and handlers for common selection operations.
 *
 * @returns {Object} Selection management utilities
 *   @property {React.RefObject} selectionRef - Ref holding current selection
 *   @property {boolean} isSelectionDisabled - Whether selection is empty/invalid
 *   @property {Function} handleSelectionChange - Callback for selection changes
 *   @property {Function} clearSelection - Clears current selection
 */
export const useSelectionState = () => {
    const selectionRef = useRef(null);
    const [isSelectionDisabled, setIsSelectionDisabled] = useState(true);

    const handleSelectionChange = useCallback((currentTableSelection) => {
        selectionRef.current = currentTableSelection;
        setIsSelectionDisabled(isSelectionEmpty(currentTableSelection));
    }, []);

    const clearSelection = useCallback(() => {
        selectionRef.current = null;
        setIsSelectionDisabled(true);
    }, []);

    return {
        selectionRef,
        isSelectionDisabled,
        handleSelectionChange,
        clearSelection,
    };
};
